import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Injector,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  HostListener
} from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Application } from '@enums/application.enums';
import { Fields, PAYMENT_REDIRECTS } from '@constants/constant';
import { PriceDiscountPipe } from '@modules/shared/pipes/price-discount.pipe';
import { ALL_AVAILABLE_PLANS_LABELS } from '@components/all-available-plans-modal/constants';
import { API_URLS, APIENDPOINT } from '@core/api-url.enum';
import { CommonService } from '@shared/services/common.service';
import { ApiCacheService, CacheTypes } from '@core/cache/api-cache.service';
import { Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromUserProfile from '@shared/user-profile.reducer';
import { MyProfile } from '@shared/pipes/my-profile.pipe';
import { SubscriptionUpgradeDialogComponent } from '@shared/components/subscription-upgrade-dialog/subscription-upgrade-dialog.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AuthService } from '@core/auth.service';
import { LoginDialogComponent } from '@shared/components/login-dialog/login-dialog.component';
import { ApiCallService } from '@services/api-call.service';
import { getTrialEndDate } from '@utils/common.utils';
import { Router } from '@angular/router';
import { PermissionService } from '@shared/services/permissions.service';
import { BenefitCodeAccordionComponent } from '@components/benefit-code-accordion/benefit-code-accordion.component';
import { CREATE_PLAN_CONSTANTS } from '@modules/monetize/components/create-plan/create-plan.constant';

@Component({
  selector: 'app-all-available-plans-modal',
  templateUrl: './all-available-plans-modal.component.html',
  styleUrls: ['./all-available-plans-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, PriceDiscountPipe, BenefitCodeAccordionComponent]
})
export class AllAvailablePlansModalComponent implements OnInit {
  @Output() planSelected = new EventEmitter<{ subscription: any; plan: any }>();
  @Output() closed = new EventEmitter<void>();

  userSubscriptionList: any[] = [];
  userProfile: any = null;
  isMyProfile = false;
  pUserSubscriptionList: any[] = [];
  userId: string = '';
  isUnlockingContent: boolean = false;
  isLivestreamPayment: boolean = false;
  subscriptionPaymentData;
  paymentRouteQueryParams;
  lockedSubscriptions;
  onPaymentComplete?: (res: any, paymentData: any) => void;

  @ViewChild('plansContainer') plansContainerRef: ElementRef<HTMLDivElement>;

  private scrollStepPx = 400;

  labels = ALL_AVAILABLE_PLANS_LABELS;
  Application = Application;
  Fields = Fields;
  private destroy$ = new Subject<void>();

  selectedPlanBySub: Record<string, any> = {};
  private readonly myProfile: MyProfile;
  selectedPlan;
  currSelectedSubscription;
  headerLabel: string = this.labels.TITLE;
  isLoading: boolean = false;
  CREATE_PLAN_CONSTANTS = CREATE_PLAN_CONSTANTS;

  constructor(
    public modalRef: BsModalRef,
    private readonly cs: CommonService,
    private readonly apiCacheService: ApiCacheService,
    private readonly store: Store<fromUserProfile.UserProfileState>,
    private readonly modalService: BsModalService,
    public readonly authService: AuthService,
    private readonly apiCallService: ApiCallService,
    private readonly ps: PermissionService,
    private readonly router: Router,
    _injector: Injector
  ) {
    this.myProfile = _injector.get<MyProfile>(MyProfile);
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') {
      this.scrollPrev();
    } else if (event.key === 'ArrowRight') {
      this.scrollNext();
    }
  }

  scrollNext(): void {
    if (!this.plansContainerRef) return;
    this.plansContainerRef.nativeElement.scrollBy({ left: this.scrollStepPx, behavior: 'smooth' });
  }

  scrollPrev(): void {
    if (!this.plansContainerRef) return;
    this.plansContainerRef.nativeElement.scrollBy({ left: -this.scrollStepPx, behavior: 'smooth' });
  }

  ngOnInit(): void {
    if (this.userId) {
      this.getUserProfile(this.userId);

      this.store
        .select(fromUserProfile.getUserProfileItems)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          if (this.myProfile.transform(this.userId, true)) {
            this.userProfile = res;
          }
          if (res?.email) {
            if (this.userId && this.myProfile.transform(this.userId, true)) {
              this.isMyProfile = true;
            } else {
              this.isMyProfile = false;
            }
          }
        });
    } else {
      this.getAllSubscriptionListOfParentUser();
    }
  }

  getUserProfile(userId) {
    this.isLoading = true;
    this.apiCacheService
      .get(APIENDPOINT.user(userId), CacheTypes.REFRESH_EVERY_N)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          this.userProfile = res.data;
          this.userSubscriptionList = res.subscriptions;
          if (this.lockedSubscriptions?.length) {
            this.userSubscriptionList = this.userSubscriptionList?.filter(
              (userSub) =>
                this.lockedSubscriptions?.some(
                  (lockedSub) => lockedSub.sk === userSub.sk
                )
            );
          }
          this.userSubscriptionList.sort((a, b) => {
            return a.duration - b.duration;
          });

          this.getAllSubscriptionListOfParentUser();
        },
        error: (err) => {
          this.isLoading = false;
          this.cs.showErrorMessage(err.message);
        }
      });
  }

  getAllSubscriptionListOfParentUser(): void {
    this.apiCacheService
      .get(
        APIENDPOINT.getAllSubscriptionListOfParentUser(
          this.cs.isWhiteLabelWeb,
          this.cs.getHostUrl
        ),
        CacheTypes.REFRESH_EVERY_N
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.handleSubscriptionListResponse(res);
        },
        error: (err) => {
          this.handleSubscriptionListError(err);
        }
      });
  }

  private handleSubscriptionListError(err: any) {
    this.pUserSubscriptionList = [];
    this.cs.showErrorMessage(err.message);
  }

  private handleSubscriptionListResponse(res: any) {
    if (res.status) {
      this.pUserSubscriptionList = res.data;
      this.userSubscriptionList.forEach((ele) => {
        if(ele?.plan_details?.length) {
          ele.plan_details.forEach((item: any) => {
            this.cs.setExperienceMerchandiseSubscribedPlanList(item);
          });
        }
        let subscribed = false;
        ele?.plan_details?.forEach((pack: any) => {
          pack.is_subscribed = this.checkSubscribeStatus(ele, pack);
          if (pack.is_subscribed) {
            subscribed = true;
          }
        });
        ele['subscribed'] = subscribed;
      });
    }
  }

  checkSubscribeStatus(obj: any, subplan: any): boolean {
    const arr = this.pUserSubscriptionList.filter(
      (ele) => ele.active && ele.subplan_id === subplan.id
    );
    return (
      arr.filter(
        (ele) =>
          ele.sk.split('#')[Application.TWO] ===
          obj.sk.split('#')[Application.ONE]
      ).length !== Application.ZERO
    );
  }

  getSelectedPlan(subscription: any): any {
    return (
      this.selectedPlanBySub[subscription.sk] ?? subscription?.plan_details?.[0]
    );
  }

  setSelectedPlan(subscription: any, plan: any): void {
    this.selectedPlanBySub = {
      ...this.selectedPlanBySub,
      [subscription.sk]: plan
    };
  }

  getDurationLabel(plan: any): string {
    if (!plan) return '';
    return plan.duration === Application.ONE
      ? `${plan.duration} ${this.labels.MONTH}`
      : `${plan.duration} ${this.labels.MONTHS}`;
  }

  isSubscribedPlan(plan: any): boolean {
    return !!plan?.is_subscribed;
  }

  getConfirmButtonLabel(plan: any): string {
    if (this.isSubscribedPlan(plan)) return this.Fields.CURRENTLY_SUBSCRIBED;
    if (plan?.trial_enable && plan?.trial_days > Application.ZERO) {
      return `${this.Fields.START} ${plan.trial_days}-${this.Fields.DAY} ${this.Fields.FREE_TRIAL}`;
    }
    return this.labels.SELECT_PLAN;
  }

  isConfirmDisabled(plan: any): boolean {
    return this.isMyProfile || this.isSubscribedPlan(plan);
  }

  onSelectPlan(subscription: any, plan: any): void {
    if (this.isConfirmDisabled(plan)) return;
    this.selectedPlan = plan;
    this.currSelectedSubscription = subscription;
    if (this.isUnlockingContent) {
      this.goToPaymentForSubscription(this.selectedPlan);
    } else this.showUnsubscribeNotification(subscription, plan);
    this.planSelected.emit({ subscription, plan });
  }

  showUnsubscribeNotification(subscription, plan) {
    if (!this.authService.isAuthenticated()) {
      this.loginDialog(true);
      this.close();
      return;
    }
    if (this.isMyProfile) {
      this.cs.showErrorMessage(Application.CANNOT_SUBSCRIBE_OWN_SUBSCRIPTION);
      return;
    }
    const cb = () => {
      const subscribedPlan = subscription?.plan_details?.filter(
        (p) => p.is_subscribed === true
      );
      if (
        subscribedPlan.length &&
        subscribedPlan[Application.ZERO].duration > this.selectedPlan.duration
      ) {
        this.close();
        const modalInfo = Object.assign({
          type: this.labels.DOWNGRADE
        });
        this.modalRef = this.modalService.show(
          SubscriptionUpgradeDialogComponent,
          {
            initialState: Object.assign({
              type: modalInfo,
              selectedPlan: plan,
              currSelectedSubscription: this.currSelectedSubscription,
              pUserSubscriptionList: this.pUserSubscriptionList
            }),
            class: 'modal-sm',
            backdrop: 'static'
          }
        );
      } else if (
        subscribedPlan.length &&
        subscribedPlan[Application.ZERO].duration < this.selectedPlan.duration
      ) {
        this.close();
        const modalInfo = Object.assign({
          type: this.labels.UPGRADE
        });
        this.modalRef = this.modalService.show(
          SubscriptionUpgradeDialogComponent,
          {
            initialState: Object.assign({
              type: modalInfo,
              selectedPlan: plan,
              currSelectedSubscription: this.currSelectedSubscription,
              pUserSubscriptionList: this.pUserSubscriptionList
            }),
            class: 'modal-sm',
            backdrop: 'static'
          }
        );
      } else if (
        this.pUserSubscriptionList.some(
          (item: any) =>
            item.active &&
            item.plan_creater_details.user_name ===
              subscription?.user_details?.user_name
        )
      ) {
        this.close();
        const modalInfo = Object.assign({
          type: 'additional'
        });
        this.modalRef = this.modalService.show(
          SubscriptionUpgradeDialogComponent,
          {
            initialState: Object.assign({
              type: modalInfo,
              selectedPlan: plan,
              currSelectedSubscription: this.currSelectedSubscription,
              pUserSubscriptionList: this.pUserSubscriptionList,
              user: this.userProfile
            }),
            class: 'modal-sm',
            backdrop: 'static'
          }
        );
      } else {
        this.goToPaymentForSubscription(this.selectedPlan);
      }

      this.modalRef?.onHidden.subscribe((res) => {
        if (res === this.labels.MOVE_AHEAD) {
          this.goToPaymentForSubscription(this.selectedPlan);
        }
      });
    };
    this.ps.checkDelegateUserPermission(cb.bind(this));
  }

  goToPaymentForSubscription(planPackege): void {
    const obj = this.currSelectedSubscription;
    const trial_end_date = getTrialEndDate(planPackege?.trial_days);
    if (!obj.is_subscribed) {
      const data = {
        post_id: null,
        type: 'subscription',
        plan_id: obj.sk,
        plan_user_id: obj.pk,
        plan_discount: planPackege.discount,
        sub_plan_id: planPackege.id,
        orderSK: null,
        trial_enable: planPackege?.trial_enable,
        trial_days: planPackege?.trial_days,
        trial_end_date,
        amount: planPackege.base_price
          ? Number(planPackege.base_price?.toString())
          : Number(planPackege.price),
        currency_symbol: planPackege?.currency_symbol,
        localPrice: Number(planPackege.price),
        exchangeRateUSD: planPackege?.exchange_rate_to_usd,
        included_experiences: planPackege.included_experiences,
        included_merchandise: planPackege.included_merchandise
      };
      this.cs.setExperienceMerchandiseSubscribedPlanList(data)
      const requestData = {
        ...data,
        ...(this.subscriptionPaymentData || {})
      };

      if (this.isLivestreamPayment) {
        this.cs.liveStreamPaymentInitiate.next(requestData);
        this.close();
        return;
      }
      this.apiCallService
        .post(API_URLS.subscribeUserPlan, requestData, true)
        .subscribe({
          next: (res) => {
            if (res.status) {
              this.close();
              requestData['plan_name'] = obj.name;
              requestData['duration'] = planPackege.duration;
              requestData['features'] = obj.features;
              requestData['user'] = obj.user_details;
              requestData['free_dms_count'] = obj.free_dms_count;
              requestData['is_unlimited_free_dms'] = obj.is_unlimited_free_dms;
              requestData.orderSK = res?.data?.orderSK;

              if (this.onPaymentComplete) {
                this.onPaymentComplete(res, requestData);
              } else {
                this.cs.subscriptionPaymentData.next(requestData);
                if (this.cs.status) {
                  this.cs.status = false;
                }
                this.cs.closeVideoPlayerModal$.next({ hide: true });
                if (this.paymentRouteQueryParams) {
                  this.router.navigate(
                    [Application.MAKE_PAYMENT],
                    this.paymentRouteQueryParams
                  );
                } else {
                  this.router.navigate([Application.MAKE_PAYMENT], {
                    queryParams: {
                      redirect: PAYMENT_REDIRECTS.otherProfile,
                      url: `${this.userProfile?.user_name}`
                    }
                  });
                }
              }
            }
          },
          error: (err) => {
            this.cs.showErrorMessage(err.message);
          }
        });
    } else {
      this.cs.showWarningMessage(ALL_AVAILABLE_PLANS_LABELS.ALREADY_SUBSCRIBED);
    }
  }

  loginDialog(status?: boolean) {
    this.modalRef = this.modalService.show(LoginDialogComponent, {
      initialState: Object.assign({ status: status })
    });
  }

  close(): void {
    this.modalRef?.hide();
    this.closed.emit();
  }
}
